import { useCallback, useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import {
  fileToAttachment,
  isSupportedImage,
  MAX_FILES,
  rehydrateStoredAttachments,
  SUPPORTED,
  type Attachment,
} from "../lib/attachments";
import { pauseReasonText } from "../lib/queuePause";
import { QUEUE_MESSAGE_MAX_BYTES } from "../config";
import { friendlyError } from "../api/errorMessages";
import type { QueuedPrompt } from "../hooks/useChatStream";

type Props = {
  /** Fully disabled (history is loading). Submitting is blocked. */
  disabled: boolean;
  /** A turn is streaming: submitting queues the prompt instead of sending now. */
  streaming?: boolean;
  onSend: (text: string, attachments: Attachment[]) => void;
  /** Prompts waiting to run, rendered as removable chips. */
  queue?: QueuedPrompt[];
  onRemoveQueued?: (id: string) => void;
  /**
   * Save an edited queued prompt in place. `attachments` is required, not
   * optional: a two-argument call reads as "just the text" but would clear the
   * prompt's images, and the type is the only thing that catches it.
   */
  onUpdateQueued?: (id: string, text: string, attachments: Attachment[]) => void;
  /**
   * Move a queued prompt to a new position (0 = runs next). Driven by the
   * per-row Move up / Move down buttons and by dragging a row onto another.
   */
  onMoveQueued?: (id: string, toIndex: number) => boolean | void;
  /**
   * Seam for tests. `fileToAttachment` needs a canvas, which happy-dom does not
   * have at all, so the intake path is untestable without injecting a stand-in.
   */
  toAttachment?: (file: File) => Promise<Attachment>;
  /** The server paused this chat's queue; shown above the chips with Resume. */
  paused?: boolean;
  /** Daemon pause reason, turned into words by pauseReasonText. */
  pausedReason?: string | null;
  onResumeQueue?: () => void;
  /** Re-send a queued prompt the server refused to save. */
  onRetryQueued?: (id: string) => void;
};

/**
 * Two lists hold the same images when every slot is the same intake (same id)
 * or the same image the daemon stored (same storedId). Order counts: the row
 * is ordered and the user can see it. The storedId half matters when a chip
 * was rebuilt from the server (a refetch mints new ids for the same stored
 * images): without it an open edit reads as modified, stops the Up/Down walk,
 * and is saved again with every image as bytes.
 */
function sameImage(x: Attachment, y: Attachment): boolean {
  return x.id === y.id || (x.storedId !== undefined && x.storedId === y.storedId);
}

function sameAttachments(a: readonly Attachment[], b: readonly Attachment[]): boolean {
  return a.length === b.length && a.every((x, i) => sameImage(x, b[i]!));
}

/**
 * What a chip is called on screen, in its tooltip, and to a screen reader.
 *
 * An image with no caption is a real prompt the daemon accepts, so a queued item
 * can legitimately have no text at all — and then the chip rendered an empty
 * button, an empty `title`, and an "Edit queued message: " that trailed off into
 * nothing. There is still something to point at, so say what it is.
 */
function chipLabel(qp: QueuedPrompt): string {
  return qp.text.trim().length > 0 ? qp.text : "(no caption)";
}

/** The transfer type a dragged queue row carries. Private, so nothing else accepts it. */
const ROW_DRAG_TYPE = "application/x-taiw-queue-row";

// Minimal typings for the (still vendor-prefixed) Web Speech API.
type SpeechRecognitionResult = {
  isFinal: boolean;
  0: { transcript: string };
};
type SpeechRecognitionEvent = {
  resultIndex: number;
  results: { length: number; [i: number]: SpeechRecognitionResult };
};
type SpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export default function Composer({
  disabled,
  streaming = false,
  onSend,
  queue = [],
  onRemoveQueued,
  onUpdateQueued,
  onMoveQueued,
  toAttachment = fileToAttachment,
  paused = false,
  pausedReason = null,
  onResumeQueue,
  onRetryQueued,
}: Props) {
  const [value, setValue] = useState("");
  // Images staged for the next send, in the order they were taken in.
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // Why the last intake refused something (too large, wrong type, over the
  // per-message cap). Null when the last intake was clean.
  const [intakeError, setIntakeError] = useState<string | null>(null);
  // An edit session: which queued chip is loaded, and the draft it displaced.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [stashedDraft, setStashedDraft] = useState("");
  const [stashedAttachments, setStashedAttachments] = useState<Attachment[]>([]);
  const [listening, setListening] = useState(false);
  // A row being dragged to a new slot, and the row it is currently over. The
  // dragged id is a ref, not state: nothing on screen changes until a drop.
  const dragIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  // Which move button to focus once the queue re-renders in its new order. A
  // moved row is re-inserted into the DOM, which drops focus, so a keyboard user
  // pressing Move up twice would otherwise lose their place after the first.
  const refocusRef = useRef<{ id: string; dir: "up" | "down" } | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);
  // The row last loaded for editing, kept past its drain so the box can be
  // compared against it after the row is gone from `queue`.
  const lastEditingRef = useRef<QueuedPrompt | null>(null);
  // What a screen reader hears after a move: the row's new slot.
  const [liveMsg, setLiveMsg] = useState("");
  // Say something through the one live region. A repeat of the same text is
  // not a DOM change, so a zero-width toggle makes it one.
  const announce = useCallback((msg: string) => {
    setLiveMsg((prev) => (prev === msg ? `${msg}\u200b` : msg));
  }, []);
  // Set while a draft's stored images are being loaded: a second Enter waits.
  const rehydratingRef = useRef(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // A synchronous mirror of `attachments`, plus a generation that moves whenever
  // the row's whole context is swapped. Both exist because intake awaits an
  // encode, and a multi-megabyte GIF spends real time in `createImageBitmap` and
  // `FileReader`: without the mirror, two intakes overlapping in that window each
  // size the cap against their own stale render, and without the generation an
  // encode resolving after a send or a chip switch writes into whichever row
  // replaced the one it started in. Same device, and the same reason, as
  // `queueRef` in useChatStream.ts.
  const rowRef = useRef<Attachment[]>([]);
  const rowGenRef = useRef(0);
  const recogRef = useRef<SpeechRecognitionInstance | null>(null);
  const baseTextRef = useRef<string>("");
  const [speechSupported] = useState<boolean>(() => getSpeechRecognitionCtor() !== null);

  // Auto-resize.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`;
  }, [value]);

  // The queued item currently loaded for editing, or null. The queue is not
  // mutated until save, so comparing `value` and `attachments` against it is the
  // "untouched" test.
  const editing = editingId === null ? null : queue.find((qp) => qp.id === editingId) ?? null;

  // After a move, put focus back on the moved row. The same-direction button is
  // preferred so repeated presses keep walking; at the end of the list it is
  // disabled, and focus goes to the row itself rather than to the opposite
  // button, which would undo the move on the next press.
  useEffect(() => {
    const want = refocusRef.current;
    if (!want) return;
    refocusRef.current = null;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-queue-id="${want.id}"]`);
    if (!row) return;
    const btn = row.querySelector<HTMLButtonElement>(`.composer-chip-move[data-move="${want.dir}"]`);
    (btn && !btn.disabled ? btn : row).focus();
    const at = queue.findIndex((qp) => qp.id === want.id);
    // A repeat of the same text is not a DOM change, so a screen reader would
    // stay silent on the second identical move; a zero-width toggle makes it one.
    if (at !== -1) setLiveMsg((prev) => {
      const msg = `Moved to position ${at + 1} of ${queue.length}`;
      return prev === msg ? `${msg}\u200b` : msg;
    });
  }, [queue]);

  // The paused banner and a refused save are both new text on screen that a
  // screen reader user would otherwise never hear about: a role="status"
  // element that mounts with its content already in it is not announced.
  const showPaused = paused && queue.length > 0;
  const announcedPauseRef = useRef<string | null>(null);
  useEffect(() => {
    const msg = showPaused ? `Queue paused: ${pauseReasonText(pausedReason)}` : null;
    if (msg !== null && msg !== announcedPauseRef.current) announce(msg);
    announcedPauseRef.current = msg;
  }, [showPaused, pausedReason, announce]);

  const failedSeenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const failed = queue.filter((qp) => qp.status === "failed");
    const fresh = failed.find((qp) => !failedSeenRef.current.has(qp.id));
    failedSeenRef.current = new Set(failed.map((qp) => qp.id));
    if (fresh) announce(`Not saved: ${chipLabel(fresh)}`);
  }, [queue, announce]);

  // The Up/Down walk changes which row is loaded while focus stays in the
  // textarea, and past the list's height cap that row can be scrolled out of
  // sight. Keep it visible. (Optional call: happy-dom has no scrollIntoView.)
  useEffect(() => {
    if (editingId === null) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-queue-id="${editingId}"]`);
    if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "nearest" });
  }, [editingId]);

  // History loading disables reordering; a drag already in flight must not
  // land after that, so it is dropped along with its highlight.
  useEffect(() => {
    if (!disabled) return;
    dragIdRef.current = null;
    setDragOverId(null);
  }, [disabled]);

  // A dragged row that drains is removed from the DOM, and `dragend` never
  // reaches a removed node, so the armed id would outlive its row and turn the
  // next hover into a phantom drop target.
  useEffect(() => {
    if (editing) lastEditingRef.current = editing;
    if (dragIdRef.current !== null && !queue.some((qp) => qp.id === dragIdRef.current)) {
      dragIdRef.current = null;
      setDragOverId(null);
    }
  }, [queue, editing]);


  // Stop recognition on unmount.
  useEffect(() => {
    return () => {
      try { recogRef.current?.stop(); } catch { /* noop */ }
    };
  }, []);

  /**
   * Update the row in place — an intake landing, a thumbnail removed. Whatever
   * is still encoding was started against this same row and may still land.
   */
  const updateRow = useCallback((next: Attachment[]) => {
    rowRef.current = next;
    setAttachments(next);
  }, []);

  /**
   * Swap the row's whole context: a send, an edit opening or closing, a walk to
   * another chip. Anything still encoding belonged to the row that just went
   * away, so the generation moves and `addFiles` refuses those results. Any
   * standing notice went with that row too.
   */
  const replaceRow = useCallback((next: Attachment[]) => {
    rowGenRef.current++;
    rowRef.current = next;
    setAttachments(next);
    setIntakeError(null);
  }, []);

  /**
   * The one place images enter the composer, from all three routes (paste, drop,
   * the picker). Unsupported files are dropped without comment — a paste often
   * carries a text/plain sibling nobody meant to attach — but anything that got
   * as far as `toAttachment` and was refused has to say why, named: since the
   * byte cap landed, an oversized screenshot is rejected here rather than 413ing
   * after send, and a rejection nobody can see looks like the app ignoring the
   * user.
   */
  const addFiles = useCallback(async (files: FileList | File[]) => {
    if (disabled) return;
    const picked = Array.from(files).filter(isSupportedImage);
    if (picked.length === 0) {
      // A new intake attempt, even one carrying nothing usable, retires whatever
      // the last one had to say.
      setIntakeError(null);
      return;
    }
    // Everything below the await has to survive the composer changing under it.
    const gen = rowGenRef.current;
    // allSettled, not all: `all` rejects on the first bad file and throws away
    // the good ones encoded alongside it, so one oversized image in a drop of
    // four would silently cost the user the other three.
    const settled = await Promise.allSettled(picked.map((f) => toAttachment(f)));
    const made: Attachment[] = [];
    const refused: string[] = [];
    settled.forEach((r, i) => {
      if (r.status === "fulfilled") made.push(r.value);
      // Named, because none of `fileToAttachment`'s messages carry a filename:
      // drop four with two oversized and two anonymous "image too large" lines
      // leave no way to tell which two were refused.
      else refused.push(`${picked[i]!.name || "image"}: ${r.reason instanceof Error ? r.reason.message : String(r.reason)}`);
    });
    if (rowGenRef.current !== gen) {
      // Sent, saved, or walked to another chip while this was encoding. Landing
      // now would attach these images to a prompt the user never put them on —
      // the same silent wrong write the queue-edit guards exist to prevent — so
      // they are dropped, and said to be dropped.
      setIntakeError(
        made.length > 0
          ? `${made[0]!.name} was not attached: the composer moved on before it finished loading`
          : refused[0] ?? null,
      );
      return;
    }
    // Off the ref, not the render: two intakes overlapping in their encode
    // windows would each size the cap against their own stale snapshot.
    if (rowRef.current.length + made.length > MAX_FILES) {
      refused.push(`only ${MAX_FILES} images fit in one message`);
    }
    if (made.length > 0) updateRow([...rowRef.current, ...made].slice(0, MAX_FILES));
    // One line, and a per-file reason ahead of the cap: naming the actual size
    // of one rejected screenshot tells the user more than a count would, and the
    // empty row alongside it already shows that nothing got through.
    setIntakeError(refused[0] ?? null);
  }, [disabled, toAttachment, updateRow]);

  const removeAttachment = useCallback((id: string) => {
    updateRow(rowRef.current.filter((a) => a.id !== id));
    // The row the notice was about no longer looks the way it did.
    setIntakeError(null);
  }, [updateRow]);

  const endEdit = useCallback(() => {
    setEditingId(null);
    setValue(stashedDraft);
    replaceRow(stashedAttachments);
    setStashedDraft("");
    setStashedAttachments([]);
  }, [replaceRow, stashedDraft, stashedAttachments]);

  // The queue drains from the head when a turn ends, so the item being edited
  // can start running mid-edit, and a move to the top makes that one click
  // away. Two cases:
  // - A text-only row whose box is untouched: the box is now a copy of the
  //   running prompt, and Enter would send it twice. Bring back the draft the
  //   edit displaced instead. Images are never discarded this way, so a row
  //   that carries any keeps the second path.
  // - Anything else: drop the session but keep the text and images, so they
  //   become an ordinary draft the user can still send.
  // Neither path is a replaceRow: the row on screen is untouched, so an encode
  // still in flight for it is still going to the right place and must be
  // allowed to land (into the restored draft, in the first case). This is why
  // the intake guard keys on the row generation rather than on `editingId`,
  // which changes here for no visible reason.
  useEffect(() => {
    if (editingId === null || queue.some((qp) => qp.id === editingId)) return;
    setEditingId(null);
    const was = lastEditingRef.current;
    const copyOfRunning = was !== null && was.id === editingId && value.trim() === was.text
      && was.attachments.length === 0 && attachments.length === 0;
    if (copyOfRunning) {
      setValue(stashedDraft);
      updateRow(stashedAttachments);
    }
    setStashedDraft("");
    setStashedAttachments([]);
  }, [queue, editingId, value, attachments, stashedDraft, stashedAttachments, updateRow]);

  // Save the open edit into its queued prompt without ending the session, so a
  // change made in the box is never left behind by something done to the row
  // itself (a switch to another chip, or a move). Unlike `submit`, an unchanged
  // or emptied edit is declined rather than saved: this is an implicit commit,
  // so there is nothing to report; Enter is the explicit save, and stays a
  // no-op on empty via submit's guard. "Empty" and "unchanged" both count the
  // images: an image with no caption is a real prompt the daemon accepts, so
  // clearing the caption of a chip that still has one is an edit to keep, and
  // removing an image without touching the caption is a change even though the
  // text is identical to what was loaded.
  // Returns the text it saved, or null when it declined.
  const commitOpenEdit = useCallback((): string | null => {
    if (!editing) return null;
    const text = value.trim();
    const emptied = !text && attachments.length === 0;
    const untouched = text === editing.text && sameAttachments(attachments, editing.attachments);
    if (emptied || untouched) return null;
    onUpdateQueued?.(editing.id, text, attachments);
    return text;
  }, [attachments, editing, onUpdateQueued, value]);

  // A commit that keeps the session open must also put the SAVED text back in
  // the box: the save trims, and a box that no longer matches its row reads as
  // "modified", which would silently stop the Up/Down walk for the session.
  const commitOpenEditInPlace = useCallback(() => {
    const saved = commitOpenEdit();
    if (saved !== null) setValue(saved);
  }, [commitOpenEdit]);

  const startEdit = useCallback((id: string) => {
    // Every click into a chip lands focus in the textarea, including a click on the
    // chip already loaded, where everything below is a no-op: a click leaves focus
    // on the button, and the placeholder's promise of Enter-to-save and
    // Esc-to-cancel only holds where handleKey is bound. Arrow-key entry is
    // already focused here by definition.
    taRef.current?.focus();
    if (id === editingId) return;
    const target = queue.find((qp) => qp.id === id);
    if (!target) return;
    if (editing) {
      // Switching chips commits the open edit, so nothing typed is lost. The
      // original stashed draft carries over untouched.
      commitOpenEdit();
    } else {
      setStashedDraft(value);
      setStashedAttachments(attachments);
    }
    setEditingId(id);
    setValue(target.text);
    replaceRow(target.attachments);
  }, [attachments, commitOpenEdit, editing, editingId, queue, replaceRow, value]);

  const move = useCallback((id: string, toIndex: number, dir: "up" | "down") => {
    if (!onMoveQueued) return;
    // A move that cannot change anything (the queue drained between render
    // and click) must not arm the refocus: with no re-render to consume it,
    // the next unrelated queue change would pull focus out of the textarea.
    const from = queue.findIndex((qp) => qp.id === id);
    if (from === -1 || toIndex < 0 || toIndex >= queue.length || toIndex === from) return;
    // The row may be the one loaded in the box. A promoted prompt can run
    // before Enter is pressed, so what is in the box goes into the queue first;
    // the session stays open on the same row. A box emptied of both text and
    // images cannot be saved, and moving the row anyway could run text the
    // user just erased, so that move is refused.
    if (editing?.id === id) {
      if (!value.trim() && attachments.length === 0) return;
      commitOpenEditInPlace();
    }
    // Arm the refocus only for a move the hook confirms: its re-render is what
    // consumes the arm. (The parent's state update is batched until this
    // handler returns, so arming after the call is still before that render.)
    if (onMoveQueued(id, toIndex) === false) return;
    refocusRef.current = { id, dir };
  }, [attachments, commitOpenEditInPlace, editing, onMoveQueued, queue, value]);

  // Native drag between rows. `dataTransfer` is absent in synthetic events and
  // in happy-dom, so every touch of it is guarded. A drag that did not start on
  // a row leaves `dragIdRef` null and is left alone, and so is any transfer
  // carrying files even while a row drag is armed: both still reach the
  // composer's own drop handling below.
  const isFileTransfer = (e: DragEvent<HTMLElement>) =>
    Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const beginDrag = (id: string, e: DragEvent<HTMLElement>) => {
    if (disabled || !onMoveQueued) { e.preventDefault(); return; }
    // A press on one of the row's action buttons, or on Retry, that travels a
    // few pixels would otherwise start a drag instead of the click. The caption
    // and the rest of the row are the handle.
    if ((e.target as HTMLElement | null)?.closest?.(".composer-chip-actions, .composer-chip-retry")) { e.preventDefault(); return; }
    dragIdRef.current = id;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      // Firefox will not start a drag that carries no data at all. A private
      // type, not text/plain: a text transfer released over the textarea would
      // be pasted into the draft by the browser.
      e.dataTransfer.setData(ROW_DRAG_TYPE, id);
    }
  };
  const dragOverRow = (id: string, e: DragEvent<HTMLElement>) => {
    if (dragIdRef.current === null || disabled || isFileTransfer(e)) return;
    // preventDefault is what makes the row a drop target; stopPropagation keeps
    // the composer's file drop-zone from also reacting to a row drag.
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };
  const dropOnRow = (toIndex: number, e: DragEvent<HTMLElement>) => {
    const dragged = dragIdRef.current;
    if (dragged === null || disabled || isFileTransfer(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragIdRef.current = null;
    setDragOverId(null);
    // `toIndex` is the target row's index at drop time, so a queue that drained
    // mid-drag still lands the row where the pointer is. A dragged row that
    // drained is unknown to the hook and moves nothing.
    if (editing?.id === dragged) {
      // Same rule as the buttons: an emptied edit is not moved.
      if (!value.trim() && attachments.length === 0) return;
      commitOpenEditInPlace();
    }
    onMoveQueued?.(dragged, toIndex);
  };
  const leaveRow = (id: string, e: DragEvent<HTMLElement>) => {
    // Moving into a child of the row is not leaving it.
    const to = e.relatedTarget as Node | null;
    if (to && e.currentTarget.contains(to)) return;
    if (dragOverId === id) setDragOverId(null);
  };
  const endDrag = () => {
    dragIdRef.current = null;
    setDragOverId(null);
  };

  // A draft can hold an image the daemon stored for a queued prompt: the chip
  // was open in the box when the server started it, and its images stayed
  // behind as a draft. Such an image has a storedId and no bytes, and /chat
  // takes bytes, so load them before the draft leaves. The fetch is async; a
  // row swapped meanwhile (a chip loaded, a conversation switch) is a new
  // draft and the old one is dropped. A load that fails keeps the draft and
  // says why; nothing is sent with an empty image.
  const sendDraft = useCallback((text: string, draft: Attachment[]) => {
    if (!draft.some((a) => a.storedId !== undefined && a.base64 === "")) {
      onSend(text, draft);
      setValue("");
      replaceRow([]);
      return;
    }
    rehydratingRef.current = true;
    const gen = rowGenRef.current;
    void rehydrateStoredAttachments(draft).then(
      (full) => {
        rehydratingRef.current = false;
        if (gen !== rowGenRef.current) return;
        onSend(text, full);
        // Keep anything typed while the images loaded.
        setValue((cur) => (cur.trim() === text ? "" : cur));
        replaceRow([]);
      },
      () => {
        rehydratingRef.current = false;
        if (gen !== rowGenRef.current) return;
        setIntakeError("A saved image could not be loaded. Remove it and attach it again.");
      },
    );
  }, [onSend, replaceRow]);

  const submit = useCallback(() => {
    const text = value.trim();
    if ((!text && attachments.length === 0) || disabled || rehydratingRef.current) return;
    // The daemon caps what its queue STORES (enqueue and edit), not a watched
    // send. On the queue path an oversized prompt is caught here, before
    // anything is cleared: the draft stays in the box and the notice says why.
    // Mirrors useChatStream's `send` routing (streaming, or an unpaused queue
    // with a prompt that is not failed).
    const queuePath = editing !== null || streaming || (!paused && queue.some((qp) => qp.status !== "failed"));
    if (queuePath && new TextEncoder().encode(text).length > QUEUE_MESSAGE_MAX_BYTES) {
      setIntakeError(friendlyError({ code: "message_too_large" }).body);
      return;
    }
    if (editing) {
      // An edit names stored images by id (`keep`); it never needs their bytes.
      onUpdateQueued?.(editing.id, text, attachments);
      endEdit();
      return;
    }
    sendDraft(text, attachments);
  }, [value, attachments, disabled, editing, streaming, paused, queue, onUpdateQueued, endEdit, sendDraft]);

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
      return;
    }
    if (e.key === "Escape" && editing) {
      e.preventDefault();
      // While a session is open Escape means cancel the edit and nothing else. The
      // queue only exists mid-turn, and ChatColumn's window listener stops that
      // turn on Escape, so without this one keypress would also abort the
      // response. With no session open the event still reaches that listener.
      e.stopPropagation();
      endEdit();
      return;
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

    if (editing) {
      // Once the text differs from what was loaded, the arrows are ordinary
      // caret movement — a multi-line edit must stay editable. Attaching or
      // removing an image counts as differing too: navigating away discards the
      // edit, and doing that to an image the user just added, on a keypress that
      // did nothing visible to the caret, is silent data loss.
      if (value !== editing.text || !sameAttachments(attachments, editing.attachments)) return;
      const i = queue.findIndex((qp) => qp.id === editing.id);
      const next = e.key === "ArrowUp" ? i - 1 : i + 1;
      e.preventDefault();
      if (next < 0) return;                     // already at the oldest
      if (next >= queue.length) { endEdit(); return; }  // past the newest
      setEditingId(queue[next]!.id);
      setValue(queue[next]!.text);
      replaceRow(queue[next]!.attachments);
      return;
    }

    // Start a session only from a genuinely empty box. An exact check, not a
    // trimmed one: a stray space is content and must not be wiped.
    if (e.key !== "ArrowUp" || value !== "" || queue.length === 0) return;
    e.preventDefault();
    startEdit(queue[queue.length - 1]!.id);
  };

  const stopListening = useCallback(() => {
    try { recogRef.current?.stop(); } catch { /* noop */ }
  }, []);

  const startListening = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const recog = new Ctor();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
    baseTextRef.current = value.length > 0 && !value.endsWith(" ") ? value + " " : value;
    recog.onresult = (e: SpeechRecognitionEvent) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        const t = r[0].transcript;
        if (r.isFinal) final += t;
        else interim += t;
      }
      setValue(baseTextRef.current + final + interim);
      if (final) baseTextRef.current += final;
    };
    recog.onerror = () => {
      setListening(false);
    };
    recog.onend = () => {
      setListening(false);
      recogRef.current = null;
    };
    recogRef.current = recog;
    try {
      recog.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, [value]);

  const toggleMic = () => {
    if (disabled) return;
    if (listening) stopListening();
    else startListening();
  };

  const placeholder = disabled
    ? "Loading history…"
    : editing
      ? "Editing queued message (Enter to save, Esc to cancel)"
      : streaming
        ? "Type to add to the queue (Enter to add)"
        : "Type a message (Enter to send, Shift+Enter for newline)";

  return (
    <div
      className="composer"
      // dragover must be cancelled for the composer to be a drop target at all.
      // The drop itself is only cancelled when it actually carries files, so
      // dragging selected text into the textarea still behaves normally.
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        // A queued row released anywhere but on another row goes nowhere, and
        // the browser must not treat its transfer as something to insert.
        if (dragIdRef.current !== null) { e.preventDefault(); endDrag(); return; }
        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;
        e.preventDefault();
        void addFiles(files);
      }}
    >
      {showPaused && (
        <div className="composer-queue-paused">
          <span>Queue paused: {pauseReasonText(pausedReason)}</span>
          {onResumeQueue && (
            <button
              type="button"
              className="composer-queue-resume"
              disabled={disabled}
              onClick={() => {
                // The banner, and this button with it, unmounts once the pause
                // clears. Put focus where the member types next, not on the page.
                taRef.current?.focus();
                onResumeQueue();
              }}
            >
              Resume
            </button>
          )}
        </div>
      )}
      {queue.length > 0 && (
        // An ordered list, top-down: row 1 runs next, a newly queued prompt
        // joins at the bottom. The list itself carries each row's position for
        // assistive tech, so the visible number is decorative.
        // role="list" is explicit because `list-style: none` makes WebKit drop
        // the implicit one, and the row positions are what the list conveys.
        <ol className="composer-queue" role="list" aria-label="Queued messages, next first" ref={listRef}>
          {queue.map((qp, i) => {
            const label = chipLabel(qp);
            const last = i === queue.length - 1;
            const canMove = !disabled && onMoveQueued !== undefined;
            return (
              <li
                className={
                  `composer-chip${qp.id === editingId ? " composer-chip-editing" : ""}` +
                  `${qp.id === dragOverId ? " composer-chip-dragover" : ""}`
                }
                key={qp.id}
                data-queue-id={qp.id}
                tabIndex={-1}
                title={label}
                aria-current={qp.id === editingId ? "true" : undefined}
                draggable={canMove}
                onDragStart={(e) => beginDrag(qp.id, e)}
                onDragOver={(e) => dragOverRow(qp.id, e)}
                onDragLeave={(e) => leaveRow(qp.id, e)}
                onDrop={(e) => dropOnRow(i, e)}
                onDragEnd={endDrag}
              >
                <span className="composer-chip-pos" aria-hidden="true">{i + 1}</span>
                {i === 0 && <span className="composer-chip-next">Next</span>}
                <button
                  type="button"
                  className="composer-chip-text"
                  disabled={disabled}
                  onClick={() => startEdit(qp.id)}
                >
                  {label}
                </button>
                {qp.status === "sending" && <span className="composer-chip-status">Saving...</span>}
                {qp.status === "failed" && (
                  <button
                    type="button"
                    className="composer-chip-retry"
                    disabled={disabled}
                    aria-label={`Not saved. Retry: ${label}`}
                    onClick={(e) => {
                      // This button unmounts once the prompt is sending again,
                      // which would drop focus to the page: keep it on the row.
                      (e.currentTarget.closest("li") as HTMLElement | null)?.focus();
                      onRetryQueued?.(qp.id);
                    }}
                  >
                    Not saved. Retry
                  </button>
                )}
                {qp.attachments.length > 0 && (
                  <span
                    className="composer-chip-count"
                    aria-label={`${qp.attachments.length} image${qp.attachments.length === 1 ? "" : "s"}`}
                  >
                    {qp.attachments.length}
                  </span>
                )}
                <span className="composer-chip-actions">
                  {onMoveQueued && (<>
                  <button
                    type="button"
                    className="composer-chip-move"
                    data-move="up"
                    aria-label={`Move up: ${label}`}
                    title="Move up"
                    disabled={disabled || i === 0}
                    onClick={() => move(qp.id, i - 1, "up")}
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M18 15l-6-6-6 6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="composer-chip-move"
                    data-move="down"
                    aria-label={`Move down: ${label}`}
                    title="Move down"
                    disabled={disabled || last}
                    onClick={() => move(qp.id, i + 1, "down")}
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  </>)}
                  <button
                    type="button"
                    className="composer-chip-edit"
                    aria-label={`Edit queued message: ${label}`}
                    title="Edit"
                    disabled={disabled}
                    onClick={() => startEdit(qp.id)}
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="composer-chip-x"
                    aria-label={`Remove queued message: ${label}`}
                    title="Remove"
                    onClick={() => onRemoveQueued?.(qp.id)}
                  >
                    ×
                  </button>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {intakeError && (
        <p className="composer-attach-error" role="alert">{intakeError}</p>
      )}
      {attachments.length > 0 && (
        <div className="composer-attachments" aria-label="Attached images">
          {attachments.map((a) => (
            <span className="composer-attachment" key={a.id}>
              <img src={a.dataUrl} alt={a.name} />
              <button
                type="button"
                className="composer-attachment-x"
                aria-label={`Remove ${a.name}`}
                onClick={() => removeAttachment(a.id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <span className="sr-only" role="status" aria-live="polite">{liveMsg}</span>
      <textarea
        ref={taRef}
        className="composer-input"
        placeholder={placeholder}
        rows={1}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKey}
        onPaste={(e) => {
          const files = e.clipboardData?.files;
          // Never preventDefault: a paste can carry both an image and its text,
          // and swallowing the event would stop the text going into the box.
          if (files && files.length > 0) void addFiles(files);
        }}
      />
      <input
        ref={fileRef}
        type="file"
        className="composer-file"
        accept={SUPPORTED.join(",")}
        multiple
        hidden
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) void addFiles(files);
          // A file input keeps its selection, so picking the same screenshot
          // twice in a row would fire no second change event.
          e.target.value = "";
        }}
      />
      <button
        type="button"
        className="composer-attach"
        disabled={disabled}
        onClick={() => fileRef.current?.click()}
        aria-label="Attach images"
        title="Attach images"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21.4 11.05 12.25 20.2a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.67 3.67 0 1 1 5.19 5.19l-9.2 9.19a1.83 1.83 0 0 1-2.59-2.59l8.49-8.48" />
        </svg>
      </button>
      {speechSupported && (
        <button
          type="button"
          className={`composer-mic${listening ? " composer-mic-active" : ""}`}
          disabled={disabled}
          onClick={toggleMic}
          aria-label={listening ? "Stop voice input" : "Start voice input"}
          title={listening ? "Stop voice input" : "Start voice input"}
        >
          {listening ? (
            <>
              <span className="composer-mic-dot" aria-hidden="true" />
              Stop
            </>
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
            </svg>
          )}
        </button>
      )}
      <button
        type="button"
        className="composer-send"
        disabled={disabled || (!value.trim() && attachments.length === 0)}
        onClick={submit}
        title={editing ? "Save the queued message" : streaming ? "Add to the queue — runs after the current response" : "Send"}
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
          <path d="M3.4 20.4 21 12 3.4 3.6 3 10l12 2-12 2z" />
        </svg>
        {editing ? "Save" : streaming ? "Queue" : "Send"}
      </button>
    </div>
  );
}
